/**
 * Signing in with Discord on the website (worker/accounts.ts). Discord's own page asks the player who they are
 * (nothing else), then sends the browser back to /signin with a one-time code; the game swaps it for a ticket and the
 * name, and signs this device in: the same player as inside Discord, on every device. A sign-in that didn't begin in
 * this browser (someone else's link, or a phone that came back in another browser) asks "sign in as …?" first.
 */

/** The sign-in this browser began (a random word Discord hands back), and when. */
const STATE_KEY = 'gp.signinState';
/** The Discord name this browser is signed in as; missing: not signed in. */
const SIGNED_IN_KEY = 'gp.signedIn';
/** What to say once the game has loaded again (after a sign-in or a sign-out reloads it). */
const NOTE_KEY = 'gp.signinNote';
const STATE_MS = 15 * 60 * 1000;

function read(store: Storage | undefined, k: string): string | null {
  try {
    return store?.getItem(k) ?? null;
  } catch {
    return null;
  }
}

function write(store: Storage | undefined, k: string, v: string | null): void {
  try {
    if (v === null) store?.removeItem(k);
    else store?.setItem(k, v);
  } catch {
    // Not kept: the sign-in still works this once.
  }
}

/** The Discord name this browser is signed in as, or null. */
export function signedInAs(): string | null {
  return read(globalThis.localStorage, SIGNED_IN_KEY) || null;
}

export function setSignedIn(name: string | null): void {
  write(globalThis.localStorage, SIGNED_IN_KEY, name);
}

/** A note for the next load (the sign-in or sign-out reloads the game), or the one left by the last. */
export function leaveNote(text: string): void {
  write(globalThis.sessionStorage, NOTE_KEY, text);
}

export function takeNote(): string | null {
  const n = read(globalThis.sessionStorage, NOTE_KEY);
  write(globalThis.sessionStorage, NOTE_KEY, null);
  return n;
}

/** Where Discord sends the browser back to: this address's /signin (registered in the Discord app's OAuth2 settings). */
export const signInReturn = (): string => `${location.origin}/signin`;

/** This page is the way back from Discord's sign-in page. */
export const BACK_FROM_DISCORD = /\/signin\/?$/.test(location.pathname);

/** Off to Discord's page ("Grand Premio wants to know who you are"), and back to /signin. */
export function beginSignIn(clientId: string, authorize: string): void {
  const state = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  write(globalThis.localStorage, STATE_KEY, JSON.stringify({ state, at: Date.now() }));
  const q = new URLSearchParams({ client_id: clientId, response_type: 'code', redirect_uri: signInReturn(), scope: 'identify', state, prompt: 'none' });
  location.assign(`${authorize}?${q}`);
}

export type SignInReturn =
  | { kind: 'cancelled' }
  | { kind: 'failed' }
  /** Who signed in (`ours`: the sign-in began in this browser, so it goes ahead without asking). */
  | { kind: 'ticket'; ticket: string; name: string; ours: boolean };

/** Back from Discord's page: the address set back to the game's own, and what Discord said. */
export async function readReturn(): Promise<SignInReturn> {
  const q = new URLSearchParams(location.search);
  history.replaceState(null, '', location.pathname.replace(/signin\/?$/, '') + location.hash);
  const code = q.get('code');
  if (!code) return { kind: q.get('error') === 'access_denied' ? 'cancelled' : 'failed' };
  let ours = false;
  try {
    const kept = JSON.parse(read(globalThis.localStorage, STATE_KEY) ?? 'null') as { state?: unknown; at?: unknown } | null;
    ours = !!kept && kept.state === q.get('state') && typeof kept.at === 'number' && Date.now() - kept.at < STATE_MS;
  } catch {
    ours = false;
  }
  write(globalThis.localStorage, STATE_KEY, null);
  try {
    const r = await fetch('api/discord/signin', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code, redirectUri: signInReturn() }) });
    if (!r.ok) return { kind: 'failed' };
    const t = (await r.json()) as { ticket?: unknown; name?: unknown };
    return typeof t.ticket === 'string' && typeof t.name === 'string' ? { kind: 'ticket', ticket: t.ticket, name: t.name, ours } : { kind: 'failed' };
  } catch {
    return { kind: 'failed' };
  }
}

/** This device signs in as the ticket's account: the player it is from now on (`joined`: the device's own laps joined it). */
export async function claim(ticket: string, playerId: string): Promise<{ id: string; name: string; joined: boolean } | null> {
  try {
    const r = await fetch('api/discord/claim', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ticket, playerId }) });
    if (!r.ok) return null;
    const t = (await r.json()) as { player?: { id?: unknown; name?: unknown }; joined?: unknown };
    return typeof t.player?.id === 'string' && typeof t.player.name === 'string' ? { id: t.player.id, name: t.player.name, joined: t.joined === true } : null;
  } catch {
    return null;
  }
}

/**
 * Signing out: this browser forgets the player's own days (their laps stay theirs, on the board and in Discord) and
 * starts again as a new guest. The settings (sound, pedals, shake, the lesson done) stay with the device.
 */
export function forgetPlayer(): void {
  const ls = globalThis.localStorage;
  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const k = ls.key(i);
      if (k?.startsWith('granpremio:v2:day:')) ls.removeItem(k);
    }
  } catch {
    // Storage gone: nothing to forget.
  }
  for (const k of ['gp.medal', 'gp.link', 'gp.sentLink', 'gp.via', 'gp.paper', 'gp.battles', 'gp.madeUp', 'gp.madeUpAsked', 'gp.refusedLap', SIGNED_IN_KEY]) write(ls, k, null);
}
